/**
 * Stale price (Figma `3609:69145` desktop, `3609:119658` mobile).
 *
 * The backend decides — the threshold is its setting (BE-212):
 *  - `fresh`        at least one source is fresh; looks exactly as today.
 *  - `outdated`     every source is outdated, within the threshold: the last
 *                   known price stays, muted, with the clock icon + tooltip.
 *  - `unconfirmed`  every source is outdated beyond the threshold: no number,
 *                   "Price not confirmed".
 * A missing field reads as `fresh`, so nothing changes until the API sends it.
 *
 * Wire shape (proposal, BE-212), on the product card `pricing`, store offers,
 * group offers, teasers and search items:
 *   price_freshness: 'fresh' | 'outdated' | 'unconfirmed'
 *   price_updated_at: ISO timestamp of the newest source (drives the date)
 * Store and group offers may send `is_outdated: boolean` instead.
 *
 * Live today (decision 9): the card `pricing` and store offers send
 * `stale: boolean` (past the server's `PRICE_STALE_AFTER_DAYS`) — read as
 * `outdated` until BE-212 sends `price_freshness`.
 */
export type PriceFreshness = 'fresh' | 'outdated' | 'unconfirmed'

export interface PriceFreshnessInfo {
  state: PriceFreshness
  /** ISO timestamp the tooltip and notes date from; null when unknown. */
  updatedAt: string | null
}

export interface PriceFreshnessWire {
  price_freshness?: string | null
  priceFreshness?: string | null
  price_updated_at?: string | null
  priceUpdatedAt?: string | null
  is_outdated?: boolean | null
  stale?: boolean | null
  updated_at?: string | null
  observed_at?: string | null
}

const STATES: readonly PriceFreshness[] = ['fresh', 'outdated', 'unconfirmed']

/** Dev-only preview: `?stale_price=outdated|unconfirmed` marks every price. */
const MOCK_PARAM = 'stale_price'
const MOCK_AGE_DAYS: Record<PriceFreshness, number> = { fresh: 0, outdated: 4, unconfirmed: 17 }

export function readPriceFreshnessMock(search: string | undefined): PriceFreshness | null {
  if (!search) return null
  const value = new URLSearchParams(search).get(MOCK_PARAM)
  return STATES.includes(value as PriceFreshness) ? (value as PriceFreshness) : null
}

function devMock(): PriceFreshness | null {
  if (!import.meta.env.DEV || typeof window === 'undefined') return null
  return readPriceFreshnessMock(window.location.search)
}

function isoDaysAgo(days: number, now = new Date()): string {
  return new Date(now.getTime() - days * 86_400_000).toISOString()
}

export interface ReadPriceFreshnessOptions {
  /** Defaults to the dev `?stale_price=` preview. */
  mock?: PriceFreshness | null
  /**
   * Also date from `updated_at` / `observed_at`. Right for the card `pricing`
   * and store offers (they mean "price observed at"); wrong for feed items,
   * where `updated_at` is the product record.
   */
  genericDates?: boolean
}

export function readPriceFreshness(
  wire: PriceFreshnessWire | null | undefined,
  { mock = devMock(), genericDates = true }: ReadPriceFreshnessOptions = {},
): PriceFreshnessInfo {
  const priceDate = wire?.price_updated_at ?? wire?.priceUpdatedAt ?? null
  const updatedAt = priceDate ?? (genericDates ? wire?.updated_at ?? wire?.observed_at ?? null : null)
  if (mock) return { state: mock, updatedAt: mock === 'fresh' ? updatedAt : isoDaysAgo(MOCK_AGE_DAYS[mock]) }

  const raw = wire?.price_freshness ?? wire?.priceFreshness
  if (STATES.includes(raw as PriceFreshness)) return { state: raw as PriceFreshness, updatedAt }
  if (wire?.is_outdated === true || wire?.stale === true) return { state: 'outdated', updatedAt }
  return { state: 'fresh', updatedAt }
}

export function isPriceStale(state: PriceFreshness | undefined): boolean {
  return state === 'outdated' || state === 'unconfirmed'
}

/**
 * Store rows: fresh stores sorted by price come first, outdated ones after
 * them, so the first row always matches the header price. Stable: the server's
 * price order is kept inside each group.
 */
export function sortFreshFirst<T extends { outdated?: boolean }>(rows: readonly T[]): T[] {
  return [...rows.filter((row) => !row.outdated), ...rows.filter((row) => row.outdated)]
}

/** The two fields product-card models carry; absent fields mean fresh. */
export interface PriceFreshnessFields {
  priceFreshness?: PriceFreshness
  priceUpdatedAt?: string | null
}

/** For card mappers: reads only `price_freshness` / `price_updated_at`. */
export function priceFreshnessFields(wire: object | null | undefined): PriceFreshnessFields {
  const info = readPriceFreshness(wire as PriceFreshnessWire | null | undefined, { genericDates: false })
  return { priceFreshness: info.state, priceUpdatedAt: info.updatedAt }
}
